import axios from 'axios';
import { Transaction, MonthlySummary, Category } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

export const transactionAPI = {
  getAll: async (): Promise<Transaction[]> => {
    const response = await api.get('/transactions');
    return response.data;
  },

  create: async (transaction: Omit<Transaction, 'id' | 'createdAt' | 'category'>): Promise<{ id: string; message: string }> => {
    const response = await api.post('/transactions', transaction);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/transactions/${id}`);
    return response.data;
  },

  getMonthly: async (year: number, month: number): Promise<Transaction[]> => {
    const response = await api.get(`/transactions/monthly/${year}/${month}`);
    return response.data;
  },

  getMonthlySummary: async (year: number, month: number): Promise<MonthlySummary> => {
    const response = await api.get(`/summary/${year}/${month}`);
    return response.data;
  },

  search: async (params: {
    dateFrom?: string;
    dateTo?: string;
    categoryId?: string;
    description?: string;
    settlementFlag?: string;
  }): Promise<Transaction[]> => {
    const response = await api.get('/transactions/search', { params });
    return response.data;
  },

  importCSV: async (file: File): Promise<{ message: string; successCount: number; errorCount: number; errors: string[] }> => {
    const formData = new FormData();
    formData.append('csvFile', file);
    
    const response = await api.post('/transactions/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export const categoryAPI = {
  getAll: async (): Promise<Category[]> => {
    const response = await api.get('/categories');
    return response.data;
  },

  getByType: async (type: 'income' | 'expense'): Promise<Category[]> => {
    const response = await api.get(`/categories/${type}`);
    return response.data;
  },

  create: async (category: Omit<Category, 'id' | 'createdAt'>): Promise<{ id: string; message: string }> => {
    const response = await api.post('/categories', category);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },
};

export default api;